export type WeekStart = 'sun' | 'mon';
export type WeekStartSetting = WeekStart | 'sunday' | 'monday';

const DEFAULT_WEEK_START: WeekStart = 'mon';

/**
 * 祝日判定に使用する国コード
 */
const SUPPORTED_HOLIDAY_COUNTRIES = new Set([
    'US',
    'CA',
    'MX',
    'JP',
    'KR',
    'TW',
    'HK',
    'MO',
    'PH',
    'TH',
    'ID',
    'MY',
    'SG',
    'CN',
    'VN',
    'IN',
    'AU',
    'NZ',
    'GB',
    'IE',
    'FR',
    'DE',
    'ES',
    'IT',
    'PT',
    'NL',
    'BE',
    'LU',
    'AT',
    'CH',
    'NO',
    'SE',
    'DK',
    'FI',
    'IS',
    'PL',
    'CZ',
    'SK',
    'HU',
    'RO',
    'BG',
    'GR',
    'HR',
    'SI',
    'RS',
    'UA',
    'RU',
    'TR',
    'BR',
    'AR',
    'CL',
    'CO',
    'PE',
    'IL',
    'SA',
    'AE',
    'QA',
    'KW',
    'BH',
]);

const LANGUAGE_DEFAULT_REGION: Record<string, string> = {
    ja: 'JP',
    en: 'US',
    es: 'ES',
    pt: 'PT',
    fr: 'FR',
    de: 'DE',
    it: 'IT',
    nl: 'NL',
    sv: 'SE',
    no: 'NO',
    da: 'DK',
    fi: 'FI',
    pl: 'PL',
    cs: 'CZ',
    sk: 'SK',
    hu: 'HU',
    ro: 'RO',
    bg: 'BG',
    el: 'GR',
    tr: 'TR',
    ru: 'RU',
    uk: 'UA',
    zh: 'CN',
    ko: 'KR',
    th: 'TH',
    id: 'ID',
    ms: 'MY',
    hi: 'IN',
    ar: 'SA',
    he: 'IL',
};

const extractLanguageAndRegion = (language?: string): { lang?: string; region?: string } => {
    if (!language) {
        return {};
    }
    const normalized = language.replace('_', '-').trim();
    if (normalized === '') {
        return {};
    }
    const [langPart, regionPart] = normalized.split('-');
    const lang = langPart?.toLowerCase();
    const region = regionPart?.toUpperCase();
    return { lang, region };
};

/**
 * 週の開始曜日を正規化する
 * @param weekStart ユーザー設定（"monday"/"sunday" など）
 * @returns 'sun' | 'mon'
 */
export const resolveWeekStart = (weekStart?: WeekStartSetting | null): WeekStart => {
    const normalized = `${weekStart ?? ''}`.trim().toLowerCase();
    if (normalized.startsWith('sun')) {
        return 'sun';
    }
    if (normalized.startsWith('mon')) {
        return 'mon';
    }
    return DEFAULT_WEEK_START;
};
/**
 * 祝日判定に使用する国コードを解決する
 * @param language i18nextの言語コード
 * @returns ISO 3166-1 alpha-2 形式の国コード。対応外の場合は null。
 */
export const resolveHolidayCountry = (language?: string): string | null => {
    const { lang, region } = extractLanguageAndRegion(language);
    if (region && SUPPORTED_HOLIDAY_COUNTRIES.has(region)) {
        return region;
    }
    if (lang) {
        const fallbackRegion = LANGUAGE_DEFAULT_REGION[lang];
        if (fallbackRegion && SUPPORTED_HOLIDAY_COUNTRIES.has(fallbackRegion)) {
            return fallbackRegion;
        }
    }
    return null;
};
